import React, { useRef, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getCartoLightAllTileUrl, CARTO_BASEMAP_ATTRIBUTION } from '../shared/cartoBasemap';

// Simple patch for Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Get marker color based on location type
const getMarkerColor = (type) => {
  switch (type?.toLowerCase()) {
    case 'accommodation':
      return 'blue';
    case 'attraction':
      return 'red';
    case 'transportation':
      return 'green';
    case 'restaurant':
      return 'orange';
    case 'destination':
      return 'violet';
    case 'day location':
      return 'gold';
    default:
      return 'gray';
  }
};

// Create custom icon for different types of locations
const createIcon = (type, isHighlighted = false, dayNumber = null) => {
  try {
    const color = isHighlighted ? 'yellow' : getMarkerColor(type);
    const iconSize = isHighlighted ? [30, 50] : [25, 41];
    
    // If day number is provided, create a DivIcon with day number badge
    if (dayNumber !== null && dayNumber !== undefined) {
      return new L.DivIcon({
        className: 'custom-marker-with-day',
        html: `
          <div style="position: relative; display: inline-block;">
            <img 
              src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png" 
              style="width: 25px; height: 41px;"
              alt="marker"
            />
            <span 
              style="
                position: absolute;
                top: -8px;
                left: 50%;
                transform: translateX(-50%);
                background-color: #fff;
                color: #000;
                border: 2px solid ${color === 'red' ? '#c33' : color === 'blue' ? '#3388ff' : color === 'green' ? '#2ecc71' : color === 'orange' ? '#ff9800' : color === 'violet' ? '#9b59b6' : color === 'gold' ? '#f1c40f' : '#666'};
                border-radius: 50%;
                width: 20px;
                height: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 11px;
                font-weight: bold;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                z-index: 1000;
              "
            >${dayNumber}</span>
          </div>
        `,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34]
      });
    }
    
    // Default icon without day number
    return new L.Icon({
      iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: iconSize,
      iconAnchor: isHighlighted ? [15, 50] : [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });
  } catch (error) {
    console.error("Error creating icon:", error);
    // Return a default icon
    return new L.Icon.Default();
  }
};

// Simple icon cache to avoid recreating the same icons
const iconCache = {};

// Get or create icon with caching
const getIcon = (type, isHighlighted = false, dayNumber = null) => {
  // For icons with day number badges, don't cache (each day needs unique icon)
  if (dayNumber !== null && dayNumber !== undefined) {
    return createIcon(type, isHighlighted, dayNumber);
  }
  
  const cacheKey = `${type}-${isHighlighted ? 'highlighted' : 'normal'}`;
  
  // If we already have this icon type in cache, return it
  if (iconCache[cacheKey]) {
    return iconCache[cacheKey];
  }
  
  // Create new icon and cache it
  const icon = createIcon(type, isHighlighted, null);
  iconCache[cacheKey] = icon;
  return icon;
};

// Map controller that fits bounds to markers and saves/restores the view state for LocationsTab
function MapController({ locations, planId }) {
  const map = useMap();
  
  // Load saved view state from localStorage when the map is initialized
  useEffect(() => {
    if (!map) return;
    
    const timer = setTimeout(() => {
      try {
        map.invalidateSize();
        
        // If we have a planId, try to load saved view state
        if (planId) {
          const storageKey = `map_view_${planId}`;
          const savedView = localStorage.getItem(storageKey);
          if (savedView) {
            const { center, zoom } = JSON.parse(savedView);
            map.setView(center, zoom);
            console.log('Restored map view for plan:', planId);
            return; // Don't fit bounds if we've restored a saved view
          }
        }
        
        // If no saved view or no planId, fit bounds to markers
        const bounds = new L.LatLngBounds();
        let hasValidLocations = false;
        
        for (const location of locations) {
          if (location.gps?.latitude && location.gps?.longitude) {
            bounds.extend([location.gps.latitude, location.gps.longitude]);
            hasValidLocations = true;
          }
        }
        
        if (hasValidLocations && bounds.isValid()) {
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      } catch (error) {
        console.error("Error updating map view:", error);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [locations, map, planId]);
  
  // Save the current view state to localStorage when the map view changes
  useEffect(() => {
    if (!map || !planId) return;
    
    const storageKey = `map_view_${planId}`;
    
    // Flag to track if the map has been interacted with by the user
    let userInteracted = false;
    
    const handleMapMove = () => {
      try {
        // Only save if user has actually interacted with the map
        if (!userInteracted) return;
        
        const center = map.getCenter();
        const zoom = map.getZoom();
        const viewState = {
          center: [center.lat, center.lng],
          zoom
        };
        localStorage.setItem(storageKey, JSON.stringify(viewState));
        console.log('Saved map view for plan:', planId);
      } catch (error) {
        console.error("Error saving map view:", error);
      }
    };
    
    // Track user interaction with the map
    const handleUserInteraction = () => {
      userInteracted = true;
    };
    
    map.on('moveend', handleMapMove);
    map.on('zoomend', handleMapMove);
    
    // Add listeners for user interaction events
    map.on('dragstart', handleUserInteraction);
    map.on('zoomstart', handleUserInteraction);
    map.on('click', handleUserInteraction);
    
    return () => {
      map.off('moveend', handleMapMove);
      map.off('zoomend', handleMapMove);
      map.off('dragstart', handleUserInteraction);
      map.off('zoomstart', handleUserInteraction);
      map.off('click', handleUserInteraction);
    };
  }, [map, planId]);
  
  return null;
}

/**
 * Map component for LocationsTab - shows all trip locations with day number badges
 */
function Map({ locations = [], height = 400, className = '', onMarkerClick, planId, selectedActivity }) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const [mapKey] = useState(Date.now());
  
  // Helper to translate activity type
  const translateActivityType = (type) => {
    if (type === 'transportation') {
      return t('planDetail.locations.transportation');
    }
    if (type === 'accommodation') {
      return t('planDetail.mainLocations.accommodation');
    }
    if (type === 'attraction') {
      return t('planDetail.locations.attractions');
    }
    return type; // Fallback to original type
  };
  
  // Process locations to ensure they have valid coordinates
  const validLocations1 = locations.filter(location => {
    return location.gps && location.gps.latitude && location.gps.longitude;
  });
  
  // Group locations by key
  const groupedLocations = Object.values(
    validLocations1.reduce((acc, location) => {
      if (!location?.key) return acc;
      const key = location.key;
      if (!acc[key]) {
        acc[key] = { ...location, days: [], dateLabels: [] };
      }
      if (location.day && !acc[key].days.includes(location.day)) {
        acc[key].days.push(location.day);
        acc[key].dateLabels.push(location.dateLabel || '');
        acc[key].days.sort((a, b) => a - b);
      }
      return acc;
    }, {})
  ).filter(location => location.days.length > 1);
  
  // Fix validLocations: remove duplicates and update day field for grouped locations
  const originalValidLocations = [...validLocations1];
  const groupedKeys = new Set(groupedLocations.map(loc => loc.key));
  const validLocations = validLocations1.filter(location => !groupedKeys.has(location?.key));
  groupedLocations.forEach(groupedLocation => {
    const firstMatch = originalValidLocations.find(loc => loc.key === groupedLocation.key);
    if (firstMatch) {
      // Get dateLabels in the same order as sorted days
      const sortedDateLabels = groupedLocation.days.map(day => {
        const dayLocation = originalValidLocations.find(loc => loc.key === groupedLocation.key && loc.day === day);
        return dayLocation?.dateLabel || '';
      });
      validLocations.push({
        ...firstMatch,
        day: groupedLocation.days.join(','),
        dateLabel: sortedDateLabels.join(', ')
      });
    }
  });
  
  // Default center if no locations
  const defaultCenter = [40.7128, -74.0060]; // New York

  return (
    <div style={{ height, width: '100%' }} className={className}>
      <MapContainer 
        key={mapKey}
        center={defaultCenter}
        zoom={5} 
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        {/* Option 1: CartoDB Positron (Clean, English labels) */}
        <TileLayer
          attribution={CARTO_BASEMAP_ATTRIBUTION}
          url={getCartoLightAllTileUrl()}
        />
        
        {/* Option 2: Uncomment this for OpenStreetMap France (better English support)
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png"
        />
        */}
        
        {/* Option 3: Uncomment this for Stamen Terrain (English labels)
        <TileLayer
          attribution='Map tiles by <a href="http://stamen.com">Stamen Design</a>, <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a> &mdash; Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}{r}.png"
        />
        */}
        <MapController locations={validLocations} planId={planId} />
        
        {validLocations.map((location, index) => {
          // Check if this location matches the selected activity
          const isHighlighted = selectedActivity && location.name === selectedActivity.name;
          
          // Get day number for badge (for LocationsTab, we show day numbers)
          const dayNumber = location.day || null;
          
          return (
            <Marker 
              key={index} 
              position={[location.gps.latitude, location.gps.longitude]}
              icon={getIcon(location.type, isHighlighted, dayNumber)}
              eventHandlers={{
                click: (e) => {
                  if (onMarkerClick) onMarkerClick(location);
                }
              }}
            >
              <Popup>
                {location.name}
                {isHighlighted && <strong> ({t('planDetail.locations.selected')})</strong>}
                <br />
                {location.address}
                {location.dateLabel && (
                  <>
                    <br />
                    {t('planDetail.locations.dayLabel')}: {location.dateLabel}
                  </>
                )}
                {location.type && (
                  <>
                    <br />
                    {translateActivityType(location.type)}
                  </>
                )}
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

export default Map; 